import { useGetFilterCountryQuery, usePostTrademarkCreateMutation } from '../../../../app/api'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { hideModal } from '../../../../app/store/reducers/AppSlice'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { FileDropzone } from '../../../../lib/ui/file-dropzone/component/FileDropzone'
import { URL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import template from '../../../../shared/assets/Template.docx'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'

interface AddTrademarkModalProps {
  data: {
    project_id: number
  }
}

export const AddTrademarkModal = ({ data }: AddTrademarkModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [createTrademark, { isLoading }] = usePostTrademarkCreateMutation()
  const { data: countries } = useGetFilterCountryQuery({})

  const [authorisationLetter, setAuthorisationLetter] = React.useState<File | null>(null)

  const {
    handleSubmit,
    register,
    control,
    formState: { isValid, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      owner: '',
      owner_website: '',
      registrations: [{ country_id: '', registration_number: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'registrations',
  })

  const registrations = useWatch({ control, name: 'registrations' })

  const submit = (values: any) => {
    createTrademark({
      body: {
        project_id: data.project_id,
        name: values.name,
        owner: values.owner,
        owner_website: values.owner_website,
        registrations: values.registrations.map((registration: any) => ({
          country_id: Number(registration.country_id),
          registration_number: registration.registration_number,
        })),
        authorisation_letter: authorisationLetter as File,
      },
    }).then(() => {
      dispatch(hideModal())
    })
  }

  return (
    <form className={'flex flex-col overflow-hidden'} onSubmit={handleSubmit(submit)}>
      <h3
        className={
          'flex min-h-[55px] w-full items-center border-b-[3px] border-b-[#00326D]/[.02] px-[24px] text-[20px] font-medium'
        }
      >
        {t('pages.project.newTrademark')}
      </h3>
      <div
        className={'flex flex-1 flex-col gap-[16px] overflow-auto px-[24px] pt-[18px] pb-[16px]'}
      >
        <BpInput
          required
          info={t('fields.trademarkNameInfo')}
          label={t('fields.trademarkName')}
          placeholder={t('fields.trademarkNamePlaceholder')}
          {...register('name', { required: t('fields.validate.required') })}
        />
        <BpInput
          required
          info={t('fields.trademarkOwnerNameInfo')}
          label={t('fields.trademarkOwnerName')}
          placeholder={t('fields.trademarkOwnerNamePlaceholder')}
          {...register('owner', { required: t('fields.validate.required') })}
        />
        <BpInput
          required
          error={errors.owner_website?.message}
          info={t('fields.trademarkOwnerWebsiteInfo')}
          label={t('fields.trademarkOwnerWebsite')}
          placeholder={t('fields.trademarkOwnerWebsitePlaceholder')}
          {...register('owner_website', {
            required: t('fields.validate.required'),
            pattern: {
              value: URL_REGEX,
              message: t('fields.validate.invalidWebsiteAddress'),
            },
          })}
        />
        <div className={'flex flex-col gap-[8px]'}>
          <div className={'flex flex-col gap-[2px]'}>
            <p
              className={
                'mb-[2px] flex items-center gap-[4px] text-[14px] font-medium leading-[24px]'
              }
            >
              {t('pages.project.countries')}
            </p>
            <p className={'whitespace-pre-line text-[13px] text-aqua-300'}>
              {t('pages.project.countriesTrademarkDescription')}
            </p>
          </div>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className={
                'flex w-full flex-col gap-[24px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px]'
              }
            >
              <div className={'flex flex-col gap-[12px]'}>
                <BpSelect
                  required
                  dropPosition={'auto'}
                  label={t('fields.country')}
                  optionRender={renderCountryOption}
                  options={countries}
                  placeholder={t('fields.selectCountry')}
                  {...register(`registrations.${index}.country_id`, {
                    required: t('fields.validate.required'),
                  })}
                />
                {!!registrations?.[index]?.country_id && (
                  <BpInput
                    required
                    className={'flex-1'}
                    label={t('fields.registrationNumber')}
                    placeholder={t('fields.egRegistrationNumber')}
                    {...register(`registrations.${index}.registration_number`, {
                      required: t('fields.validate.required'),
                    })}
                  />
                )}
                {fields.length > 1 && (
                  <button
                    className={'w-fit text-[12px] font-medium text-danger-500'}
                    type={'button'}
                    onClick={() => remove(index)}
                  >
                    {t('pages.project.removeCountry')}
                  </button>
                )}
              </div>
            </div>
          ))}
          <BpButton
            className={'w-fit'}
            iconLeft={<IconPlus />}
            size={'xs'}
            theme={'outline'}
            type={'button'}
            onClick={() => append({ country_id: '', registration_number: '' })}
          >
            {t('pages.project.addCountry')}
          </BpButton>
        </div>
        <div className={'flex flex-col gap-[8px]'}>
          <div className={'flex flex-col gap-[2px]'}>
            <p
              className={
                'mb-[2px] flex items-center gap-[4px] text-[14px] font-medium leading-[24px]'
              }
            >
              {t('pages.project.authorisationLetter')}
            </p>
            <p className={'whitespace-pre-line text-[13px] text-aqua-300'}>
              {t('pages.project.authorisationLetterDescription')}
            </p>
          </div>
          <FileDropzone
            extensions={['pdf', 'docx']}
            maxSizeMb={50}
            value={authorisationLetter}
            onChange={setAuthorisationLetter}
          />
          <div>
            <button
              className={'text-focus'}
              type={'button'}
              onClick={() => {
                const link = document.createElement('a')
                link.href = template
                link.download = 'Trademark Authorisation Letter - Template.docx'
                link.click()
              }}
            >
              Download a template
            </button>
          </div>
        </div>
      </div>
      <div
        className={
          'flex w-full justify-between gap-[12px] border-t-[3px] border-t-[#00326D]/[.02] px-[24px] py-[24px]'
        }
      >
        <BpButton
          className={'uppercase'}
          disabled={!isValid || !authorisationLetter || isLoading}
          loading={isLoading}
          type={'submit'}
        >
          {t('buttons.create')}
        </BpButton>
        <BpButton
          className={'uppercase'}
          disabled={isLoading}
          theme='outline'
          type='button'
          onClick={() => dispatch(hideModal())}
        >
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </form>
  )
}
